import { createRouter, createWebHistory } from 'vue-router';
import routes from '@/router/routes.js';
import { useGtm } from '@gtm-support/vue-gtm';
import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalytics.js';
import { useTidio } from '@/stores/Tidio';
import { createCustomPageViewEvent } from '@/plugins/gtm/events';
import useEditionStageStore from '@/stores/EditionStage';
import { storeToRefs } from 'pinia';
import { EditionStage } from '@/types/backend';
import ROUTE_NAMES from '@/constants/routeNames';

const baseDirectory = process.env.VUE_APP_PUBLIC_PATH === undefined ? '/' : process.env.VUE_APP_PUBLIC_PATH;

const router = createRouter({
    routes,
    history: createWebHistory(baseDirectory),
    scrollBehavior () {
        return { left: 0, top: 0, behavior: 'smooth' };
    }
});

router.beforeEach(async (to, from, next) => {
    const editionStageStore = useEditionStageStore();
    const {
        editionStage,
        isCampaignUnderway
    } = storeToRefs(editionStageStore);

    if (!editionStage.value) {
        try {
            await editionStageStore.fetchEditionStage(router);
        } catch {
            return next({ name: ROUTE_NAMES.HOME });
        }
    }
    const tidio = useTidio();
    tidio.initChatOnPage(to);

    if (to.name === ROUTE_NAMES.HOME_SECURE) {
        if (editionStage.value === EditionStage.beforeCampaignSecure ||
            editionStage.value === EditionStage.afterCampaignSecure) {
            editionStageStore.setPasswordPopup(true);
            next();
        } else {
            return next({ name: ROUTE_NAMES.HOME });
        }
    }

    if (!isCampaignUnderway.value) {
        let pagesToCheckStage = [
            ROUTE_NAMES.FAMILIES_LIST
        ];

        if (editionStage.value === EditionStage.beforeCampaign) {
            pagesToCheckStage = [
                ROUTE_NAMES.FAMILIES_LIST,
                ROUTE_NAMES.FAMILY,
                ROUTE_NAMES.FAMILY_CHOICE_FORM,
                ROUTE_NAMES.THANKS
            ];
        }

        if (pagesToCheckStage.includes(String(to.name))) {
            return next({ name: ROUTE_NAMES.HOME });
        }
    }

    document.title = to.meta.title != null ? to.meta.title : 'Szlachetna Paczka';

    const nearestWithMeta = to.matched.slice().reverse().find(r => r.meta && r.meta.metaTags);
    Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(el => el.parentNode.removeChild(el));

    if (!nearestWithMeta) {
        return next();
    }

    nearestWithMeta.meta.metaTags.map(tagDef => {
        const tag = document.createElement('meta');

        Object.keys(tagDef).forEach(key => {
            tag.setAttribute(key, tagDef[key]);
        });

        tag.setAttribute('data-vue-router-controlled', '');
        return tag;
    }).forEach(tag => document.head.appendChild(tag));

    next();
});

router.afterEach((to) => {
    const gtm = useGtm();

    const googleAnalyticsStore = useGoogleAnalyticsStore();
    const { ga } = googleAnalyticsStore;

    const gtmEventData = {
        fullPath: to.fullPath,
        meta: to.meta,
        params: {
            ...to.params,
            ga
        }
    };

    gtm.trackEvent(createCustomPageViewEvent(gtmEventData));
});

export default router;
